import { FunctionsHttpError } from "@supabase/supabase-js";
import { CorpAdminMapper } from "@/adapters/CorpAdmin.mapper";
import { zCorpAdminCreateResultDto, zCorpAdminSetCompaniesResultDto, } from "@/types/company/corpAdmin.dto";
import { toB2BError, toDataShapeError } from "@/utils/b2bErrors";
import { lazyService } from "@/utils/lazyMock";
import { supabase } from "@/utils/supabase";
/** Ошибка edge-функции: тело `{error: СИМВОЛ, details?}` читается из `FunctionsHttpError.context`. */
const edgeError = async (error) => {
    if (error instanceof FunctionsHttpError) {
        const response = error.context;
        try {
            const body = response ? await response.clone().json() : null;
            if (body && typeof body === "object") {
                return toB2BError({ ...body, code: response ? String(response.status) : null });
            }
        }
        catch {
            // тело не JSON — ниже общий разбор
        }
    }
    return toB2BError(error);
};
const invoke = async (body) => {
    const { data, error } = await supabase().functions.invoke("corp-admin-manage", { body });
    if (error)
        throw await edgeError(error);
    return data;
};
export class CorpAdminService {
    async getCompanyAdmins(companyId) {
        const { data, error } = await supabase()
            .from("v_corp_admins")
            .select("*")
            .contains("company_ids", [companyId])
            .order("created_at", { ascending: false });
        if (error)
            throw toB2BError(error);
        try {
            return (data ?? []).map(CorpAdminMapper.rowToDomain);
        }
        catch (parseError) {
            throw toDataShapeError(parseError);
        }
    }
    async createAdmin(input) {
        const data = await invoke({
            action: "create",
            email: input.email,
            password: input.password,
            fullName: input.fullName,
            phone: input.phone,
            companyIds: input.companyIds,
        });
        return { adminId: zCorpAdminCreateResultDto.parse(data).adminId };
    }
    async blockAdmin(adminId, reason) {
        await invoke({ action: "block", adminId, reason });
    }
    async unblockAdmin(adminId) {
        await invoke({ action: "unblock", adminId });
    }
    async setPassword(adminId, password) {
        await invoke({ action: "set_password", adminId, password });
    }
    async setCompanies(adminId, companyIds) {
        const { data, error } = await supabase().rpc("corp_admin_set_companies", {
            p_admin_id: adminId,
            p_company_ids: companyIds,
        });
        if (error)
            throw toB2BError(error);
        return zCorpAdminSetCompaniesResultDto.parse(data).companyIds ?? [];
    }
}
// Литерал REACT_APP_B2B_MOCK — см. CompanyService.ts / utils/lazyMock.ts (G4).
export const corpAdminService = process.env.REACT_APP_B2B_MOCK === "true"
    ? lazyService(() => import("@/services/mock/CorpAdminServiceMock").then((module) => module.corpAdminServiceMock))
    : new CorpAdminService();
